// © 2015-2021 WDNA - All Rights Reserved

import _ from 'lodash';
import * as React from 'react';

import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { messageError, messageInfo, messageWarning, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { sensor } from '../../requests';

interface Props {
  sensorId: number;
  show?: boolean;
  title?: string;
  onCancel: (e?: React.MouseEvent<HTMLButtonElement>) => void;
}

interface State {
  data?: string;
}

export default class ChripStack extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.state = {
      data: undefined
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  private onSend = (): void => {
    const { sensorId, onCancel } = this.props;
    const { data } = this.state;

    if (_.isEmpty(data)) {
      messageWarning('Warning', 'Data to enqueue cannot be empty');
      return;
    }

    this.loadingStart();
    sensor('chirpstack_set', String(sensorId), { data })
      ?.then(() => {
        messageInfo('Success', 'Data successfully enqueued', 5000);
        this.setLocalState({ data: undefined });
        onCancel();
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  private onFlush = (): void => {
    const { sensorId, onCancel } = this.props;
    messageBoxWarning('Delete', 'Do you want flush the queue ?', () => {
      this.loadingStart();
      sensor('chirpstack_del', String(sensorId))
        ?.then(() => {
          messageInfo('Success', 'ChirpStack queue flushed for device', 5000);
          onCancel();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  private onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public loadingStart = (): void => {
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    window.store.dispatch(finishProcess(this.pid));
  };

  public render(): JSX.Element {
    const { show, title, onCancel } = this.props;
    const { data } = this.state;

    return (
      <ContainerModal
        fullScreenButton={false}
        show={show}
        title={title}
        modalClass="selector-modal"
        onClose={onCancel}
        footerButtons={[
          {
            text: 'Close',
            className: 'btn btn-primary',
            onClick: onCancel,
          },
          {
            text: 'Send',
            className: 'btn btn-primary',
            onClick: this.onSend,
          },
          {
            text: 'Flush',
            className: 'btn btn-primary',
            onClick: this.onFlush,
          },
        ]}
      >
        <fieldset>
          <div className="row">
            <div style={{ width: '100%', padding: '25px' }}>
              <label className="label">
                <b>Data</b>
              </label>
              &nbsp;
              <input
                id="data"
                name="data"
                type="text"
                value={_.defaultTo(data, '')}
                onChange={this.onChange}
                className="input-new"
                style={{ width: '99%' }}
              />
            </div>
          </div>
        </fieldset>
      </ContainerModal>
    );
  }
}
